import { useMemo } from "react";
import clsx from "clsx";
import { type BusinessSubscription, type BusinessInvitation, type BusinessUser, Business } from "lens-platform-sdk";

import { isPendingSubscriptionInvitation, PendingSubscriptionInvitation } from "src/services/getInvitationState";
import { isActiveSeat, isCanceledSeat } from "src/services/getSubscriptionSeatState";
import { getSubscriptionSeatCount } from "src/services/getSubscriptionSeatCount";
import { isAssignableSubscription } from "src/services/getSubscriptionState";
import { useAnalytics } from "src/hooks/useAnalytics";

import ActiveSeatTable from "src/components/Business/ActiveSeatTable";
import CanceledSeatTable from "src/components/Business/CanceledSeatTable";
import PendingUserTable from "src/components/Business/PendingUserTable";
import { TabList, TabGroup, Tab, TabPanel, TabPanels } from "src/components/Tabs/Tabs";

import styles from "src/components/Business/AssigneeTable.module.css";
import { ActivationLinkInvitation, getActivationLinkInvitation } from "src/hooks/useGetActivationLinkInvitation";

interface Props {
  businessSubscriptions: BusinessSubscription[];
  businessInvitations: BusinessInvitation[];
  businessUsers: BusinessUser[];
  selectedTableTabIndex?: number;
  setSelectedTableTabIndex: (index: number) => void;
  onRemoveBusinessInvitation?: (invitation: PendingSubscriptionInvitation | ActivationLinkInvitation) => void;
  onUnassignSeat?: (id: string) => void;
  loading?: boolean;
  business: Business | null | undefined;
}

/**
 * The tabbed table to be used on the `business/:businessId/subscriptions` page
 */
const AssigneeTable = ({
  businessSubscriptions,
  businessInvitations,
  businessUsers,
  selectedTableTabIndex,
  setSelectedTableTabIndex,
  onRemoveBusinessInvitation,
  onUnassignSeat,
  loading,
  business,
}: Props) => {
  const { trackButtonClicked } = useAnalytics();
  const activationLinkInvitation = useMemo(() => {
    if (business && businessSubscriptions) {
      return getActivationLinkInvitation(business, businessSubscriptions);
    }

    return null;
  }, [business, businessSubscriptions]);

  const pendingSubscriptionInvitations = useMemo(
    () => businessInvitations.filter(isPendingSubscriptionInvitation),
    [businessInvitations],
  );
  const numberOfPendingInvitations = activationLinkInvitation
    ? pendingSubscriptionInvitations.length + 1
    : pendingSubscriptionInvitations.length;

  const numberOfActiveSeats = useMemo(
    () =>
      businessSubscriptions.reduce((previous, current) => previous + current.usedSeats.filter(isActiveSeat).length, 0),
    [businessSubscriptions],
  );

  const numberOfCanceledeSeats = useMemo(
    () =>
      businessSubscriptions.reduce(
        (previous, current) => previous + current.usedSeats.filter(isCanceledSeat).length,
        0,
      ),
    [businessSubscriptions],
  );

  const assignableBusinessSubscriptions = useMemo(
    () => businessSubscriptions.filter(isAssignableSubscription),
    [businessSubscriptions],
  );
  const seatCount = useMemo(
    () => getSubscriptionSeatCount(businessInvitations, assignableBusinessSubscriptions),
    [businessInvitations, assignableBusinessSubscriptions],
  );

  const tabs = useMemo(
    () => [
      { label: `Active (${numberOfActiveSeats})`, key: "active" },
      { label: `Pending (${numberOfPendingInvitations})`, key: "pending" },
      { label: `Cancelled (${numberOfCanceledeSeats})`, key: "cancelled" },
    ],
    [numberOfPendingInvitations, numberOfActiveSeats, numberOfCanceledeSeats],
  );

  const handleChange = (index: number) => {
    setSelectedTableTabIndex(index);
    trackButtonClicked(tabs[index]!.label, { type: "tab" });
  };

  return (
    <section className={styles.assigneeTable}>
      <header className={styles.tableTitleBar}>
        <h2 className={clsx("lds-h2-caps", styles.tableTitle)}>Manage Seats</h2>
        <div
          className={styles.tableTitleAssignNumber}
          aria-label={`${seatCount.unavailable} out of ${seatCount.total} seats assigned`}
        >
          <span aria-hidden>{`${seatCount.unavailable} / ${seatCount.total}`}</span>
          <span aria-hidden>Seats assigned</span>
        </div>
      </header>
      <TabGroup defaultIndex={0} selectedIndex={selectedTableTabIndex} onChange={handleChange}>
        <div className={styles.tableTab}>
          <span className={styles.tableSubTitle}>Assign seats from your subscriptions.</span>
          <TabList className={styles.tableTabButtonBar}>
            {tabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </TabList>
        </div>
        <TabPanels>
          <TabPanel>
            <ActiveSeatTable
              onUnassignSeat={onUnassignSeat}
              businessSubscriptions={businessSubscriptions}
              businessUsers={businessUsers}
            />
          </TabPanel>
          <TabPanel>
            <PendingUserTable
              businessSubscriptions={businessSubscriptions}
              pendingSubscriptionInvitations={
                activationLinkInvitation
                  ? [...pendingSubscriptionInvitations, activationLinkInvitation]
                  : pendingSubscriptionInvitations
              }
              onRemoveBusinessInvitation={onRemoveBusinessInvitation}
              loading={loading}
            />
          </TabPanel>
          <TabPanel>
            <CanceledSeatTable businessSubscriptions={businessSubscriptions} />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </section>
  );
};

export default AssigneeTable;
